package org.maplibre.compose.expressions.dsl

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.TextUnitType
import kotlin.jvm.JvmName
import kotlin.time.Duration
import org.maplibre.compose.expressions.ast.BooleanLiteral
import org.maplibre.compose.expressions.ast.ColorLiteral
import org.maplibre.compose.expressions.ast.DpLiteral
import org.maplibre.compose.expressions.ast.DpOffsetLiteral
import org.maplibre.compose.expressions.ast.DpPaddingLiteral
import org.maplibre.compose.expressions.ast.DpTextOffsetCalculation
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.ast.FloatLiteral
import org.maplibre.compose.expressions.ast.IntLiteral
import org.maplibre.compose.expressions.ast.ListLiteral
import org.maplibre.compose.expressions.ast.Literal
import org.maplibre.compose.expressions.ast.MillisecondsLiteral
import org.maplibre.compose.expressions.ast.NullLiteral
import org.maplibre.compose.expressions.ast.OffsetLiteral
import org.maplibre.compose.expressions.ast.ProjectionTransitionLiteral
import org.maplibre.compose.expressions.ast.StringLiteral
import org.maplibre.compose.expressions.ast.TextUnitCalculation
import org.maplibre.compose.expressions.ast.TextUnitOffsetCalculation
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.ColorValue
import org.maplibre.compose.expressions.value.DpOffsetValue
import org.maplibre.compose.expressions.value.DpPaddingValue
import org.maplibre.compose.expressions.value.DpValue
import org.maplibre.compose.expressions.value.EnumValue
import org.maplibre.compose.expressions.value.ExpressionValue
import org.maplibre.compose.expressions.value.FloatOffsetValue
import org.maplibre.compose.expressions.value.FloatValue
import org.maplibre.compose.expressions.value.IntValue
import org.maplibre.compose.expressions.value.ListValue
import org.maplibre.compose.expressions.value.MillisecondsValue
import org.maplibre.compose.expressions.value.NullValue
import org.maplibre.compose.expressions.value.ProjectionValue
import org.maplibre.compose.expressions.value.StringValue
import org.maplibre.compose.expressions.value.SymbolAnchor
import org.maplibre.compose.expressions.value.TextUnitOffsetValue
import org.maplibre.compose.expressions.value.TextUnitValue
import org.maplibre.compose.expressions.value.TextVariableAnchorOffsetValue
import org.maplibre.compose.expressions.value.VectorValue
import org.maplibre.compose.style.ProjectionTransition
import org.maplibre.compose.util.DpPadding
import org.maplibre.compose.util.ExperimentalMaplibreComposeApi

/** Creates a literal expression for a [String] value. */
public fun const(string: String): Expression<StringValue> = StringLiteral.of(string)

/** Creates a literal expression for a named style value. */
public fun <T : EnumValue> const(value: T): Expression<T> = StringLiteral.of(value.value).cast()

/** Creates a literal expression for a dimensionless [Float] value. */
public fun const(float: Float): Expression<FloatValue> = FloatLiteral.of(float)

/** Creates a literal expression for an dimensionless [Int] value. */
public fun const(int: Int): Expression<IntValue> = IntLiteral.of(int)

/** Creates a literal expression for a [Dp] value. */
public fun const(dp: Dp): Expression<DpValue> = DpLiteral.of(dp)

/**
 * Creates a literal expression for a specified [TextUnit] value in SP or EM. It can be provided in
 * either unit, and will resolve to one at runtime depending on the property it is used in.
 */
public fun const(textUnit: TextUnit): Expression<TextUnitValue> = TextUnitCalculation.of(textUnit)

/**
 * Creates a literal expression for a [Duration] value.
 *
 * The duration will be rounded to the nearest whole milliseconds.
 */
public fun const(duration: Duration): Expression<MillisecondsValue> =
  MillisecondsLiteral.of(duration)

/** Creates a literal expression for a [Boolean] value. */
public fun const(bool: Boolean): Expression<BooleanValue> = BooleanLiteral.of(bool)

/** Creates a literal expression for a [Color] value. */
public fun const(color: Color): Expression<ColorValue> = ColorLiteral.of(color)

/** Creates a literal expression for an [Offset] value. */
public fun const(offset: Offset): Expression<FloatOffsetValue> = OffsetLiteral.of(offset)

/** Creates a literal expression for a [DpOffset] value. */
public fun const(dpOffset: DpOffset): Expression<DpOffsetValue> = DpOffsetLiteral.of(dpOffset)

/** Creates a literal expression for a [DpPadding] value. */
public fun const(padding: DpPadding): Expression<DpPaddingValue> = DpPaddingLiteral.of(padding)

/** Creates a literal expression for a [ProjectionTransition] value. */
@ExperimentalMaplibreComposeApi
public fun const(transition: ProjectionTransition): Expression<ProjectionValue> =
  ProjectionTransitionLiteral.of(transition)

internal fun <T : ExpressionValue?> const(list: List<Literal<T, *>>): ListLiteral<T> =
  ListLiteral.of(list)

/** Creates a literal expression for a list of strings. */
@JvmName("constStringList")
public fun const(list: List<String>): Expression<ListValue<StringValue>> =
  const(list.map { StringLiteral.of(it) })

/** Creates a literal expression for a list of named style values. */
@JvmName("constEnumList")
public fun <T : EnumValue> const(list: List<T>): Expression<ListValue<T>> =
  const(list.map { StringLiteral.of(it.value).cast<T>() })

/** Creates a literal expression for a list of numbers. */
@JvmName("constNumberList")
public fun const(list: List<Number>): Expression<VectorValue<Number>> =
  const(list.map { FloatLiteral.of(it.toFloat()) }).cast()

/**
 * Creates an expression for [TextVariableAnchorOffsetValue], used by
 * [SymbolLayer][org.maplibre.compose.layers.SymbolLayer]'s `textVariableAnchorOffset` parameter.
 *
 * Offsets use the same SP, EM, and DP conversion as [textOffset], relative to the layer's text
 * size. Anchors may repeat, and their order determines the placement preference.
 */
public fun textVariableAnchorOffset(
  vararg pairs: Pair<SymbolAnchor, Expression<TextUnitOffsetValue>>,
): Expression<TextVariableAnchorOffsetValue> =
  list(pairs.flatMap { (anchor, offset) -> listOf(const(anchor), offset) }).cast()

/** Creates a literal expression for a 2D [Offset]. */
public fun offset(x: Float, y: Float): Expression<FloatOffsetValue> = OffsetLiteral.of(Offset(x, y))

/** Creates a literal expression for a 2D [DpOffset]. */
public fun offset(x: Dp, y: Dp): Expression<DpOffsetValue> = DpOffsetLiteral.of(DpOffset(x, y))

/**
 * Creates a text offset with a fixed DP distance, independent of font scale and label size.
 *
 * As with SP offsets, the layer's text size must not use zoom interpolation.
 *
 * Use the [TextUnit] overload for offsets that scale with accessibility text size (SP) or the
 * label's text size (EM).
 */
public fun textOffset(x: Dp, y: Dp): Expression<TextUnitOffsetValue> = DpTextOffsetCalculation(x, y)

/**
 * Creates a text offset in SP or EM. Both components must have the same [TextUnitType].
 *
 * SP offsets scale with accessibility text size; EM offsets also scale with the label's text size.
 */
public fun textOffset(x: TextUnit, y: TextUnit): Expression<TextUnitOffsetValue> =
  TextUnitOffsetCalculation.of(x, y)

/** Creates a literal expression for a [DpPadding] value. */
public fun padding(left: Dp, top: Dp, right: Dp, bottom: Dp): Expression<DpPaddingValue> =
  const(DpPadding(left = left, top = top, right = right, bottom = bottom))

/**
 * Creates a literal expression for a `null` value.
 *
 * MapLibre accepts a `null` literal only where it expects a value of unknown type, so the result
 * has the type [NullValue] and is not assignable to a nullable expression type such as
 * `Expression<StringValue?>`. See [NullValue] for where it can be used. To leave a layer property
 * unset, pass Kotlin `null` to the layer instead.
 */
public fun nil(): Expression<NullValue> = NullLiteral
